/* eslint-disable import/prefer-default-export */
import axios from 'axios';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  FetchSinglePostRequest,
} from 'redux/schemas/api/post';
import { AIDiscussionInsights, AIDiscussionPrompts, Post } from 'redux/schemas/models/post';
import { makeQueryParamString } from 'redux/actions/helpers';
import { RootState } from 'redux/schemas';
import { NovoAIErrorCodes, NovoAIPayload } from 'redux/schemas/models/activity';
import { ComponentTrueType, TypeFromTrueType } from 'redux/schemas/models/lecture-component';
import t from 'react-translate';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { addAlertMessage } from './alert-messages';

const PERSPECTIVES_SESSION_STORAGE_PREFIX = 'discussion-insights-perspectives';
const PERSPECTIVES_SESSION_STORAGE_TTL_MS = 5 * 60 * 1000;

type PerspectivesPayload = {
  similarReply: AIDiscussionInsights['perspectives']['similar'],
  differentReply: AIDiscussionInsights['perspectives']['different']
};

type PerspectivesCacheEntry = PerspectivesPayload & {
  cachedAt: number,
  commentsCount: number | null,
  commentIdsSignature: string | null
};

const getTopicPerspectivesCachePrefix = (catalogId: string, topicId: number) => (
  `${PERSPECTIVES_SESSION_STORAGE_PREFIX}:${catalogId}:${topicId}:`
);

const getPerspectivesCacheKey = (catalogId: string, topicId: number, commentId: number) => (
  `${getTopicPerspectivesCachePrefix(catalogId, topicId)}${commentId}`
);

const getTopicCommentsCount = (state: RootState, topicId: number) => state.models.posts[topicId]?.postsCount ?? null;
const getTopicCommentIdsSignature = (state: RootState, topicId: number) => {
  const commentIds = state.models.posts[topicId]?.commentIds;
  if (!commentIds?.length) return null;

  return [...commentIds].sort((firstId, secondId) => firstId - secondId).join(',');
};

const clearCachedPerspectivesForComment = (catalogId: string, topicId: number, commentId: number) => {
  window.sessionStorage.removeItem(getPerspectivesCacheKey(catalogId, topicId, commentId));
};

export const clearDiscussionInsightsPerspectivesCache = (
  catalogId: string,
  topicId: number,
  commentId?: number,
) => {
  try {
    if (commentId != null) {
      clearCachedPerspectivesForComment(catalogId, topicId, commentId);
      return;
    }

    const cacheKeyPrefix = getTopicPerspectivesCachePrefix(catalogId, topicId);
    for (let i = window.sessionStorage.length - 1; i >= 0; i -= 1) {
      const cacheKey = window.sessionStorage.key(i);
      if (cacheKey?.startsWith(cacheKeyPrefix)) {
        window.sessionStorage.removeItem(cacheKey);
      }
    }
  } catch {
    // Ignore storage failures and fall back to normal network behavior.
  }
};

const readCachedPerspectives = (
  catalogId: string,
  topicId: number,
  commentId: number,
  commentsCount: number | null,
  commentIdsSignature: string | null,
): null | PerspectivesPayload => {
  try {
    const cacheKey = getPerspectivesCacheKey(catalogId, topicId, commentId);
    const rawCacheEntry = window.sessionStorage.getItem(cacheKey);
    if (!rawCacheEntry) return null;

    const cachedEntry = JSON.parse(rawCacheEntry) as Partial<PerspectivesCacheEntry>;
    const cacheAgeMs = Date.now() - (cachedEntry.cachedAt ?? 0);
    const cacheMatchesDiscussionState = cachedEntry.commentsCount === commentsCount;
    const cacheMatchesCommentIds = cachedEntry.commentIdsSignature == null
      || commentIdsSignature == null
      || cachedEntry.commentIdsSignature === commentIdsSignature;
    const cacheIsFresh = cacheAgeMs >= 0 && cacheAgeMs <= PERSPECTIVES_SESSION_STORAGE_TTL_MS;

    if (!cacheMatchesDiscussionState || !cacheMatchesCommentIds || !cacheIsFresh) {
      clearDiscussionInsightsPerspectivesCache(catalogId, topicId, commentId);
      return null;
    }

    if (cachedEntry.commentIdsSignature == null && commentIdsSignature != null) {
      window.sessionStorage.setItem(cacheKey, JSON.stringify({
        ...cachedEntry,
        commentIdsSignature,
      }));
    }

    return {
      similarReply: cachedEntry.similarReply ?? {},
      differentReply: cachedEntry.differentReply ?? {},
    };
  } catch {
    return null;
  }
};

const writeCachedPerspectives = (
  catalogId: string,
  topicId: number,
  commentId: number,
  commentsCount: number | null,
  commentIdsSignature: string | null,
  payload: PerspectivesPayload,
) => {
  try {
    const cacheEntry: PerspectivesCacheEntry = {
      ...payload,
      commentsCount,
      commentIdsSignature,
      cachedAt: Date.now(),
    };

    window.sessionStorage.setItem(
      getPerspectivesCacheKey(catalogId, topicId, commentId),
      JSON.stringify(cacheEntry),
    );
  } catch {
    // Ignore storage failures and fall back to normal network behavior.
  }
};

export const fetchSinglePost = createAsyncThunk<Post, FetchSinglePostRequest>(
  'FETCH_SINGLE_POST',
  async (params) => {
    const { includeUserBar } = params;
    const queryString = makeQueryParamString({ includeUserBar });
    const response = await axios.get(`${params.catalogId}/topics/${params.postId}?${queryString}`);
    /* Temporary placeholder to update points and progress on timeline manager with new points data
    * Tracked in https://novoed.atlassian.net/browse/NOV-66502. Need to be called after response for
    * fetch single post returns
    */
    params.callback?.(response.data.result);
    return response.data.result;
  },
);

// Discussion GenAI: Generate prompts
export const fetchAIGeneratedPrompts = createAsyncThunk(
  'GET_GEN_AI_PROMPTS',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const catalogId = state.app.currentCatalogId;
    const selectedContent = state.app.lecturePage.novoAI.content;

    const aiProperties: Partial<NovoAIPayload<TypeFromTrueType<ComponentTrueType>>> = {
      aiOriginTarget: {
        id: selectedContent.id,
        type: selectedContent.type,
      },
    };

    return axios.post(
      `${catalogId}/topics/ai_new`,
      { aiProperties },
    ).then(
      (response) => {
        const prompts: AIDiscussionPrompts['prompts'] = response?.data.result.map(prompt => ({
          id: prompt.index + 1,
          text: prompt.body,
        }));

        return {
          errorCode: null,
          prompts,
        };
      },
    ).catch((e) => {
      let errorCode = '';

      if (!e.response?.data?.error?.code) {
        errorCode = e.code;
      } else {
        errorCode = e.response?.data?.error?.code;
      }

      return {
        errorCode,
        prompts: [],
      };
    });
  },
);

// Discussion GenAI: Generate insights
export const resetDiscussionInsights = createAction<{ topicId: number }>('RESET_DISCUSSION_INSIGHTS');

export const updateDiscussionInsights = createAction<{ topicId: number, data: AIDiscussionInsights }>('UPDATE_DISCUSSION_INSIGHTS');

export const enableInsightsToLearners = createAsyncThunk(
  'ENABLE_INSIGHTS_TO_LEARNERS',
  async (params: { discussionId: number, topicId: number, hasAiInsights: boolean }, { getState, dispatch, rejectWithValue }) => {
    const catalogId = (getState() as RootState).app.currentCatalogId;
    const { downcasedPluralized } = (getState() as RootState).models.courses[catalogId]?.learnersName;

    return axios.post(
      `${catalogId}/topics/${params.topicId}/toggle_has_ai_insights`,
      {
        has_ai_insights: params.hasAiInsights,
      },
    )
      .then(
        response => {
          if (response.data.result) {
            dispatch(addAlertMessage({
              type: AlertMessageType.SUCCESS,
              header: t.FORM.SUCCESS_BANG(),
              message: params.hasAiInsights ? t.LECTURE_PAGES.COMPONENTS.DISCUSSION.INSIGHTS.SETTINGS.SUCCESS_ENABLE({ learnersAlias: downcasedPluralized }) : t.LECTURE_PAGES.COMPONENTS.DISCUSSION.INSIGHTS.SETTINGS.SUCCESS_DISABLE({ learnersAlias: downcasedPluralized }),
            }));
          }

          return response.data.result;
        },
      )
      .catch(e => {
        dispatch(addAlertMessage({
          type: AlertMessageType.ERROR,
          header: t.FORM.ERROR_SOMETHING_WRONG(),
        }));

        return rejectWithValue(e.response.data.error);
      });
  },
);

export const getKeyThemes = createAsyncThunk<
{ result: null | AIDiscussionInsights['keyThemes']['data'], status: number },
{ topicId: number }
>(
  'GET_KEY_THEMES',
  async (params, { getState, dispatch, rejectWithValue }) => {
    const catalogId = (getState() as RootState).app.currentCatalogId;

    return axios.post(
      `${catalogId}/topics/${params.topicId}/key_themes`,
    )
      .then(
        response => ({
          result: response.data.result?.keyThemes,
          status: response.status,
        }),
      )
      .catch(e => {
        dispatch(addAlertMessage({
          type: AlertMessageType.ERROR,
          header: t.FORM.ERROR_SOMETHING_WRONG(),
        }));

        return rejectWithValue(e.response.data.error);
      });
  },
);

export const getPerspectives = createAsyncThunk<
PerspectivesPayload,
{ topicId: number, commentId: number, force?: boolean, commentsCount?: number },
{ state: RootState }
>(
  'GET_PERSPECTIVES',
  async (params, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const catalogId = state.app.currentCatalogId;
    const commentsCount = params.commentsCount ?? getTopicCommentsCount(state, params.topicId);
    const commentIdsSignature = params.commentsCount == null
      ? getTopicCommentIdsSignature(state, params.topicId)
      : null;

    if (params.force) {
      clearDiscussionInsightsPerspectivesCache(catalogId, params.topicId, params.commentId);
    } else {
      const cachedPerspectives = readCachedPerspectives(
        catalogId,
        params.topicId,
        params.commentId,
        commentsCount,
        commentIdsSignature,
      );

      if (cachedPerspectives) return cachedPerspectives;
    }

    return axios.get(
      `${catalogId}/posts/${params.commentId}/similar_and_different`,
    )
      .then(response => {
        writeCachedPerspectives(
          catalogId,
          params.topicId,
          params.commentId,
          commentsCount,
          commentIdsSignature,
          response.data.result,
        );

        return response.data.result;
      })
      .catch(e => rejectWithValue(e.response.data.error));
  },
  {
    condition: ({ topicId, commentId, force }, { getState }) => {
      if (force) return true;

      const perspectives = getState().app.generatedAIContent.discussionInsights[topicId]?.perspectives;
      if (!perspectives) return true;

      const isDuplicateInFlight = perspectives.loading && perspectives.requestedCommentId === commentId;
      const isAlreadyLoaded = perspectives.loadedCommentId === commentId;
      const isKnownTooShort = perspectives.failedCommentId === commentId && perspectives.errorCode === NovoAIErrorCodes.TOO_SHORT;

      return !(isDuplicateInFlight || isAlreadyLoaded || isKnownTooShort);
    },
  },
);
